import React, { ChangeEvent, RefObject } from "react";
import { Film, ImagePlus, Trash2, UploadCloud } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Modal } from "../../../components/ui/modal";
import SelectInput from "../../../components/select-input";
import SearchableSelectInput from "../../../components/searchable-select-input";
import ProductOrderDetailsDrawer from "../../../components/product-order-details-drawer";
import { ShopRecordStatus } from "../../../lib/api";

interface ShopModalsProps {
  isCategoryModalOpen: boolean;
  onCloseCategoryModal: () => void;
  editingCategoryId: string | null;
  categoryName: string;
  onCategoryNameChange: (value: string) => void;
  categorySortOrder: string;
  onCategorySortOrderChange: (value: string) => void;
  categoryStatus: ShopRecordStatus;
  onCategoryStatusChange: (value: ShopRecordStatus) => void;
  savingCategory: boolean;
  onSaveCategory: () => void;

  isProductModalOpen: boolean;
  onCloseProductModal: () => void;
  editingProductId: string | null;
  productCategoryId: string;
  onProductCategoryIdChange: (value: string) => void;
  productCategoryOptions: Array<{ value: string; label: string }>;
  productName: string;
  onProductNameChange: (value: string) => void;
  productSummary: string;
  onProductSummaryChange: (value: string) => void;
  productDetail: string;
  onProductDetailChange: (value: string) => void;
  productPurchasePrice: string;
  onProductPurchasePriceChange: (value: string) => void;
  productOfferPrice: string;
  onProductOfferPriceChange: (value: string) => void;
  productStatus: ShopRecordStatus;
  onProductStatusChange: (value: ShopRecordStatus) => void;
  savingProduct: boolean;
  onSaveProduct: () => void;
  productImages: string[];
  productVideos: string[];
  uploadingImage: boolean;
  uploadingVideo: boolean;
  productImageInputRef: RefObject<HTMLInputElement | null>;
  productVideoInputRef: RefObject<HTMLInputElement | null>;
  onProductImageSelect: (e: ChangeEvent<HTMLInputElement>) => void;
  onRemoveProductImage: (url: string) => void;
  onProductVideoSelect: (e: ChangeEvent<HTMLInputElement>) => void;
  onRemoveProductVideo: (url: string) => void;

  deletingCategoryId: string | null;
  onCloseDeleteCategory: () => void;
  deletingCategory: boolean;
  onDeleteCategory: () => void;

  deletingProductId: string | null;
  onCloseDeleteProduct: () => void;
  deletingProduct: boolean;
  onDeleteProduct: () => void;

  selectedOrderId: string | null;
  onCloseOrderDetails: () => void;
}

const ShopModals: React.FC<ShopModalsProps> = ({
  isCategoryModalOpen,
  onCloseCategoryModal,
  editingCategoryId,
  categoryName,
  onCategoryNameChange,
  categorySortOrder,
  onCategorySortOrderChange,
  categoryStatus,
  onCategoryStatusChange,
  savingCategory,
  onSaveCategory,
  isProductModalOpen,
  onCloseProductModal,
  editingProductId,
  productCategoryId,
  onProductCategoryIdChange,
  productCategoryOptions,
  productName,
  onProductNameChange,
  productSummary,
  onProductSummaryChange,
  productDetail,
  onProductDetailChange,
  productPurchasePrice,
  onProductPurchasePriceChange,
  productOfferPrice,
  onProductOfferPriceChange,
  productStatus,
  onProductStatusChange,
  savingProduct,
  onSaveProduct,
  productImages,
  productVideos,
  uploadingImage,
  uploadingVideo,
  productImageInputRef,
  productVideoInputRef,
  onProductImageSelect,
  onRemoveProductImage,
  onProductVideoSelect,
  onRemoveProductVideo,
  deletingCategoryId,
  onCloseDeleteCategory,
  deletingCategory,
  onDeleteCategory,
  deletingProductId,
  onCloseDeleteProduct,
  deletingProduct,
  onDeleteProduct,
  selectedOrderId,
  onCloseOrderDetails,
}) => (
  <>
    <Modal
      isOpen={isCategoryModalOpen}
      onClose={onCloseCategoryModal}
      title={editingCategoryId ? "Edit Category" : "Add Category"}
    >
      <div className="p-6 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="category-name">Name</Label>
          <Input
            id="category-name"
            value={categoryName}
            onChange={(e) => onCategoryNameChange(e.target.value)}
            placeholder="e.g. Scaffolding"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="category-sort">Sort order (optional)</Label>
          <Input
            id="category-sort"
            type="number"
            min={0}
            step={1}
            value={categorySortOrder}
            onChange={(e) => onCategorySortOrderChange(e.target.value)}
            placeholder="Lower numbers appear first"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Status</Label>
          <SelectInput
            options={[
              { value: "ACTIVE", label: "Active" },
              { value: "DISABLED", label: "Disabled" },
            ]}
            value={categoryStatus}
            onChange={(v) => onCategoryStatusChange(v as ShopRecordStatus)}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCloseCategoryModal}>
            Cancel
          </Button>
          <Button disabled={savingCategory} onClick={() => void onSaveCategory()}>
            {savingCategory ? "Saving..." : editingCategoryId ? "Update" : "Create"}
          </Button>
        </div>
      </div>
    </Modal>

    <Modal
      isOpen={isProductModalOpen}
      onClose={onCloseProductModal}
      title={editingProductId ? "Edit Product" : "Add Product"}
    >
      <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
        <div className="space-y-1.5">
          <Label>Category</Label>
          <SearchableSelectInput
            options={productCategoryOptions}
            value={productCategoryId}
            defaultLabel="Select category"
            onSelect={onProductCategoryIdChange}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="product-name">Name</Label>
          <Input
            id="product-name"
            value={productName}
            onChange={(e) => onProductNameChange(e.target.value)}
            placeholder="Product name"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="product-summary">Summary</Label>
          <Input
            id="product-summary"
            value={productSummary}
            onChange={(e) => onProductSummaryChange(e.target.value)}
            placeholder="Short description"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="product-detail">Detail</Label>
          <textarea
            id="product-detail"
            value={productDetail}
            onChange={(e) => onProductDetailChange(e.target.value)}
            placeholder="Full description"
            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="product-purchase">Purchase Price</Label>
            <Input
              id="product-purchase"
              type="number"
              min={0}
              value={productPurchasePrice}
              onChange={(e) => onProductPurchasePriceChange(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="product-offer">Offer Price</Label>
            <Input
              id="product-offer"
              type="number"
              min={0}
              value={productOfferPrice}
              onChange={(e) => onProductOfferPriceChange(e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Status</Label>
          <SelectInput
            options={[
              { value: "ACTIVE", label: "Active" },
              { value: "DISABLED", label: "Disabled" },
            ]}
            value={productStatus}
            onChange={(v) => onProductStatusChange(v as ShopRecordStatus)}
          />
        </div>

        {editingProductId && (
          <div className="space-y-3 pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <Label>Images</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={uploadingImage}
                onClick={() => productImageInputRef.current?.click()}
              >
                <UploadCloud className="h-4 w-4 mr-1.5" />
                {uploadingImage ? "Uploading..." : "Upload"}
              </Button>
              <input
                ref={productImageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={onProductImageSelect}
              />
            </div>
            {productImages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 border border-dashed border-gray-200 rounded-lg text-gray-400">
                <ImagePlus className="h-8 w-8 mb-2" />
                <p className="text-sm">No images yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {productImages.map((url) => (
                  <div
                    key={url}
                    className="relative group rounded-lg border border-gray-100 overflow-hidden bg-gray-50"
                  >
                    <img src={url} alt="Product" className="w-full h-28 object-cover" />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="absolute top-2 right-2 h-7 w-7 p-0 border-red-200 text-red-700 bg-white/90 opacity-0 group-hover:opacity-100 transition-opacity"
                      disabled={uploadingImage}
                      onClick={() => void onRemoveProductImage(url)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {editingProductId && (
          <div className="space-y-3 pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <Label>Videos</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={uploadingVideo}
                onClick={() => productVideoInputRef.current?.click()}
              >
                <UploadCloud className="h-4 w-4 mr-1.5" />
                {uploadingVideo ? "Uploading..." : "Upload"}
              </Button>
              <input
                ref={productVideoInputRef}
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                className="hidden"
                onChange={onProductVideoSelect}
              />
            </div>
            {productVideos.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 border border-dashed border-gray-200 rounded-lg text-gray-400">
                <Film className="h-8 w-8 mb-2" />
                <p className="text-sm">No videos yet</p>
                <p className="text-xs mt-1">MP4, WebM, or MOV up to 100MB</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {productVideos.map((url) => (
                  <div
                    key={url}
                    className="relative group rounded-lg border border-gray-100 overflow-hidden bg-gray-50"
                  >
                    <video
                      src={url}
                      controls
                      className="w-full h-40 object-cover bg-black"
                      preload="metadata"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="absolute top-2 right-2 h-7 w-7 p-0 border-red-200 text-red-700 bg-white/90 opacity-0 group-hover:opacity-100 transition-opacity"
                      disabled={uploadingVideo}
                      onClick={() => void onRemoveProductVideo(url)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!editingProductId && (
          <p className="text-xs text-gray-500">
            Save the product first, then edit it to upload images and videos.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCloseProductModal}>
            Cancel
          </Button>
          <Button disabled={savingProduct} onClick={() => void onSaveProduct()}>
            {savingProduct ? "Saving..." : editingProductId ? "Update" : "Create"}
          </Button>
        </div>
      </div>
    </Modal>

    <Modal
      isOpen={deletingCategoryId !== null}
      onClose={onCloseDeleteCategory}
      title="Delete category?"
      description="This fails if products still use it. This action cannot be undone."
      className="max-w-md"
    >
      <div className="px-6 pt-5 pb-8 flex justify-end gap-3 flex-wrap shrink-0">
        <Button type="button" variant="outline" onClick={onCloseDeleteCategory} disabled={deletingCategory}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={() => void onDeleteCategory()}
          disabled={deletingCategory || !deletingCategoryId}
        >
          {deletingCategory ? "Deleting…" : "Yes, delete category"}
        </Button>
      </div>
    </Modal>

    <Modal
      isOpen={deletingProductId !== null}
      onClose={onCloseDeleteProduct}
      title="Delete product?"
      description="This action cannot be undone."
      className="max-w-md"
    >
      <div className="px-6 pt-5 pb-8 flex justify-end gap-3 flex-wrap shrink-0">
        <Button type="button" variant="outline" onClick={onCloseDeleteProduct} disabled={deletingProduct}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={() => void onDeleteProduct()}
          disabled={deletingProduct || !deletingProductId}
        >
          {deletingProduct ? "Deleting…" : "Yes, delete product"}
        </Button>
      </div>
    </Modal>

    <ProductOrderDetailsDrawer orderId={selectedOrderId} onClose={onCloseOrderDetails} />
  </>
);

export default ShopModals;
